import { useState, useRef, useCallback, useEffect } from 'react'

interface Suggestion {
  shortLabel: string
  cityPostal: string
  fullAddress: string
}

const cache = new Map<string, Suggestion[]>()

async function fetchSuggestions(query: string): Promise<Suggestion[]> {
  const key = query.trim().toLowerCase()
  if (cache.has(key)) return cache.get(key)!
  try {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 8000)
    const res = await fetch(
      `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=6&autocomplete=1`,
      { headers: { Accept: 'application/json' }, signal: controller.signal }
    )
    clearTimeout(timer)
    if (!res.ok) return []
    const data = await res.json() as { features?: unknown[] }
    const features = (data?.features ?? []) as Array<{ properties?: Record<string, string> }>
    if (features.length === 0) return []
    const results: Suggestion[] = features
      .map(f => {
        const p = f.properties ?? {}
        const num = p.housenumber ?? ''
        const street = p.street ?? p.name ?? ''
        const shortLabel = [num, street].filter(Boolean).join(' ') || (p.label ?? '')
        const postcode = p.postcode ?? ''
        const city = p.city ?? ''
        const cityPostal = [postcode, city].filter(Boolean).join(' ')
        const fullAddress = p.label ?? [shortLabel, cityPostal].filter(Boolean).join(', ')
        return { shortLabel, cityPostal, fullAddress }
      })
      .filter(s => s.fullAddress.length > 3)
    cache.set(key, results)
    return results
  } catch {
    return []
  }
}

interface AddressAutocompleteProps {
  label?: string
  value: string
  onChange: (address: string) => void
  placeholder?: string
  id?: string
}

export default function AddressAutocomplete({
  label,
  value,
  onChange,
  placeholder = '14 rue de Zurich, Strasbourg...',
  id,
}: AddressAutocompleteProps) {
  const [text, setText] = useState(value)
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [loading, setLoading] = useState(false)
  const [noResult, setNoResult] = useState(false)
  const [open, setOpen] = useState(false)
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null)
  const skip = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!text && value) setText(value)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleChange = useCallback((t: string) => {
    setText(t)
    onChange(t)
    skip.current = false
    setNoResult(false)
    if (debounce.current) clearTimeout(debounce.current)
    if (t.trim().length < 4) {
      setSuggestions([])
      setLoading(false)
      setOpen(false)
      return
    }
    setLoading(true)
    debounce.current = setTimeout(async () => {
      if (skip.current) return
      const r = await fetchSuggestions(t)
      if (!skip.current) {
        setSuggestions(r)
        setNoResult(r.length === 0)
        setLoading(false)
        setOpen(true)
      }
    }, 400)
  }, [onChange])

  const handleSelect = useCallback((s: Suggestion) => {
    skip.current = true
    if (debounce.current) clearTimeout(debounce.current)
    setText(s.fullAddress)
    setSuggestions([])
    setLoading(false)
    setNoResult(false)
    setOpen(false)
    onChange(s.fullAddress)
  }, [onChange])

  return (
    <div ref={containerRef} className='relative'>
      {label && (
        <label htmlFor={id} className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
          {label}
        </label>
      )}
      <div className='relative'>
        <input
          id={id}
          type='text'
          value={text}
          onChange={e => handleChange(e.target.value)}
          placeholder={placeholder}
          autoComplete='off'
          className='w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-dark-100 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all text-sm pr-8'
        />
        {loading && (
          <div className='absolute right-3 top-1/2 -translate-y-1/2'>
            <div className='w-4 h-4 border-2 border-primary-400 border-t-transparent rounded-full animate-spin' />
          </div>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <ul className='absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-dark-50 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl overflow-hidden'>
          {suggestions.map((s, i) => (
            <li key={i}>
              <button
                type='button'
                onMouseDown={() => handleSelect(s)}
                className='w-full text-left px-4 py-2.5 text-sm text-gray-800 dark:text-gray-200 hover:bg-primary-50 dark:hover:bg-primary-500/10 transition-colors border-b border-gray-100 dark:border-gray-800 last:border-0'
              >
                {s.fullAddress}
              </button>
            </li>
          ))}
        </ul>
      )}

      {open && noResult && (
        <div className='absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-dark-50 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl px-4 py-3 text-sm text-gray-500 dark:text-gray-400'>
          Aucune adresse trouvée
        </div>
      )}
    </div>
  )
}
